import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useInvoiceListAPI } from "@/app/hooks/api_hooks/Invoice/useInvoiceListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import React, { useEffect } from "react";
import { IInvoiceTabProps } from "../../interface";
import { FiDownload } from "react-icons/fi";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";

const InvoiceTab: React.FC<IInvoiceTabProps> = (_props) => {
  const fetchInVoicelist = useInvoiceListAPI();
  const isLoading = useLoader("isInvoiceInProgress");
  const { veticDateFormat } = useUtils();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { invoice, customerId } = routeState.state as ICustomerProfileState;
  const { NO_APPOINTMENTS } = useImageConstants();

 useEffect(() => {
    if (!customerId) return;
    fetchInVoicelist(customerId);
  }, [customerId, fetchInVoicelist]);

  return (
    <div className="h-full px-2 pt-2">
      <div className="pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pb-2 gap-2">
          <div>
            <h1 className="text-sm font-semibold leading-6 text-gray-900 mt-2">Invoice Details</h1>
            <p className="mt-1 text-xs text-gray-700">A list of all the invoice the customer has with it's pets.</p>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="mt-2">
          <TableSkeleton />
          {/* <SkeletonType1 className="mt-2" /> */}
        </div>
      ) : invoice?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[720px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {["SN", "Pet Name", "Date & Time", "Download"].map((label) => (
                      <th
                        key={label}
                        scope="col"
                        className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoice?.map((inv, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === invoice.length - 1;
                    const isSingle = invoice.length === 1;

                    return (
                      <tr
                        key={`invoice:${inv?.pet_id}:${idx + 1}`}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black 
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {idx + 1}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{inv?.pet_name}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          {inv?.created_at ? veticDateFormat(inv?.created_at) : "NA"}
                        </td>
                        <td
                          className={`
                            whitespace-nowrap px-8 py-3 text-xs text-black 
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          {inv?.invoice_link ? (
                            <a target="_blank" rel="noreferrer" href={inv?.invoice_link} className="inline-flex">
                              <span className="text-xl text-blue-500">
                                <FiDownload />
                              </span>
                            </a>
                          ) : (
                            "NA"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2 bg-white rounded-lg px-2 py-3">
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Invoices Found" />
        </div>
      )}
    </div>
  );
};

export default InvoiceTab;
